import * as React from "react";
import {
    ApiContext,
    CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS,
    getBridgeSdk,
    PublicApiContext,
} from "@swiggy-private/checkout-core";
import { useMountedRef, useEffectOnce, captureError } from "@swiggy-private/core-ui";
import { API_SUCCESS_STATUS_CODE } from "@payment-client/constants/StatusCodes";
import { OOPS_SOMETHING_WENT_WRONG } from "@payment-client/constants/TextDict";
import { Result, Err, Ok, None, Option, Some } from "@swiggy-private/js-fp";
import { validatePayLaterOtp, linkPayLater } from "@payment-client/apis/PayLater";
import { linkPayLaterPublic, validatePayLaterOtpPublic } from "@payment-client/apis/PublicPayLater";
import { LINK_ACCOUNT_SOURCE } from "@swiggy-private/native-bridge";
import { Tenant } from "@swiggy-private/native-bridge/dist/interfaces/Tenant";
import { useContext, useMemo } from "react";
import { isPublicApiEnabled } from "@swiggy-private/core-utils";
import { ShowPaymentContext } from "@payment-client/contexts/ShowPaymentContext";
import { isEmpty } from "@swiggy-private/js-utils";

interface UsePayLaterVerifyOtpReturn {
    verifyInProgress: boolean;
    resendInProgress: boolean;
    verifyOtp: (otp: string) => Promise<Result<boolean, string>>;
    resendOtp: () => Promise<void>;
}

export const usePayLaterVerifyOtp = (payLaterCode: string, mobileNumber: string): UsePayLaterVerifyOtpReturn => {
    const isMountedRef = useMountedRef();
    const { host } = React.useContext(ApiContext);
    const publicApiConfig = useContext(PublicApiContext);
    const { paymentResponse } = useContext(ShowPaymentContext);
    const [state, setState] = React.useState<Option<object>>(None);
    const [verifyInProgress, setVerifyInProgress] = React.useState(false);
    const [resendInProgress, setResendInProgress] = React.useState(false);

    const _isPublicApiEnabled = useMemo(
        () => isPublicApiEnabled(publicApiConfig || {}, CHECKOUT_WEB_PUBLIC_ENDPOINT_KEYS.PAYLATER_APIS, payLaterCode),
        [payLaterCode, publicApiConfig],
    );

    const sendOtp = React.useCallback(async () => {
        try {
            const linkPayLaterFn = _isPublicApiEnabled ? linkPayLaterPublic : linkPayLater;
            const res = await linkPayLaterFn(host.unwrap(), payLaterCode, mobileNumber);
            return isMountedRef.current && setState(Some(res || {}));
        } catch (err) {
            return captureError(err);
        }
    }, [_isPublicApiEnabled, host, payLaterCode, mobileNumber, isMountedRef]);

    useEffectOnce(() => {
        sendOtp();
    });

    const resendOtp = React.useCallback(async () => {
        if (resendInProgress) {
            return;
        }

        setResendInProgress(true);
        sendOtp().then((_) => isMountedRef.current && setResendInProgress(false));
    }, [isMountedRef, resendInProgress, sendOtp]);

    const verifyOtp = React.useCallback(
        async (otp: string): Promise<Result<boolean, string>> => {
            if (state.isNone()) {
                return Err(OOPS_SOMETHING_WENT_WRONG);
            }

            setVerifyInProgress(true);
            let otpResponse = Ok<boolean, string>(true);

            try {
                const validatePayLaterOtpFn = _isPublicApiEnabled ? validatePayLaterOtpPublic : validatePayLaterOtp;

                const response = await validatePayLaterOtpFn(host.unwrap(), {
                    payLaterCode,
                    otp,
                    state: state.unwrapOr({}),
                });

                if (response.statusCode != API_SUCCESS_STATUS_CODE) {
                    throw response;
                }
                otpResponse = Ok(true);
                getBridgeSdk()
                    .sendLinkAccountStatusToNative({
                        paymentCode: payLaterCode,
                        status: "SUCCESS",
                        source: LINK_ACCOUNT_SOURCE.PAYMENTS,
                        linkedMobileNumber: mobileNumber,
                        ...(!isEmpty(paymentResponse) &&
                            !isEmpty(paymentResponse?.tenant) && {
                                tenant: paymentResponse.tenant as Tenant,
                            }),
                    })
                    .catch((err) => {
                        __DEV__ && console.error(err);
                    });
            } catch (err) {
                otpResponse = Err(err.statusMessage || OOPS_SOMETHING_WENT_WRONG);
            }

            isMountedRef.current && setVerifyInProgress(false);
            return otpResponse;
        },
        [state, isMountedRef, _isPublicApiEnabled, host, payLaterCode, mobileNumber, paymentResponse],
    );

    return {
        verifyInProgress,
        resendInProgress,
        verifyOtp,
        resendOtp,
    };
};
